import { useId } from "react";

export type NewSiteProcessStep = { n: string; title: string; body: string };

export const newSiteProcessSteps: NewSiteProcessStep[] = [
  { n: "01", title: "Install", body: "Deploy autonomous solar-powered cameras where visibility is needed most in just minutes." },
  { n: "02", title: "Configure", body: "Set capture schedules, privacy controls, and access permissions from the cloud." },
  { n: "03", title: "AI Analysis", body: "Receive AI-driven alerts and notifications when activity changes or exceptions are detected." },
  { n: "04", title: "Integrate", body: "Connect with existing workflows, systems, and teams to keep operations aligned." },
];

/** "Operating Model" steps — second instance of the same section shell on the home page. */
export const operatingModelSteps: NewSiteProcessStep[] = [
  { n: "01", title: "Capture", body: "Collect a current image on a schedule, on demand or when a rule or external event makes a new view relevant." },
  { n: "02", title: "Interpret", body: "Compare against previous condition, apply human review or AI, and determine whether the image represents an exception." },
  { n: "03", title: "Act", body: "Escalate, dispatch, clear, correct or simply continue monitoring. Informed action ensures the right response at the right time." },
  { n: "04", title: "Evidence", body: "Retain a time-stamped visual record or condition, decision and intervention for reporting, assurance and learning." },
];

type Props = {
  eyebrow?: string;
  title?: string;
  /** Pass an empty string to leave the intro line out. */
  intro?: string;
  steps?: NewSiteProcessStep[];
  /** "gradient" = home (51:2602); "plum" = Our Solution Desktop/Steps (51:5407). */
  tone?: "gradient" | "plum";
};

/** Figma "Process" section shell, shared by the private home page and Our Solution. */
export default function NewSiteProcessSection({
  eyebrow = "Process",
  title = "Easy to use",
  intro = "Snapshot Live is designed to deploy quickly and scale across remote sites without complex infrastructure.",
  steps = newSiteProcessSteps,
  tone = "gradient",
}: Props) {
  const plum = tone === "plum";
  // Unique per instance so two copies on one page don't share a heading id.
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className={`${plum ? "ns-steps-bg" : "ns-process-bg"} flex flex-col gap-16 px-6 md:px-[72px] py-20 md:py-[120px] text-ns-white md:min-h-[727px]`}
    >
      <div className="flex flex-col items-center gap-3 text-center text-ns-white">
        <p className="text-[12px] font-medium leading-[19.1px]">{eyebrow}</p>
        <h2 id={headingId} className="font-archivo text-[28px] font-medium leading-[1.15] tracking-normal md:text-[40px]">
          {title}
        </h2>
        {intro ? <p className={`w-full ${plum ? "max-w-[580px]" : "max-w-[426px]"} text-[15px] font-medium leading-[1.5]`}>{intro}</p> : null}
      </div>
      <div className="h-px w-full bg-ns-white/30" />
      <div className="grid gap-8 md:grid-cols-4">
        {steps.map((s) => (
          <div key={s.n} className="flex min-w-0 flex-col items-start gap-4">
            <p className={`text-[15px] font-medium leading-[1.5] ${plum ? "text-ns-orange" : "text-ns-lime"}`}>{s.n}</p>
            <h3 className="font-archivo text-[27px] font-medium leading-[1.3] tracking-normal">{s.title}</h3>
            <p className="text-[15px] font-medium leading-[1.5]">{s.body}</p>
          </div>
        ))}
      </div>
      <div className="h-px w-full bg-ns-white/30" />
    </section>
  );
}
